/**
 * Umleitungen der abgelösten Seiten in die Sektion „Anfos & Umsatz" (P5 Kern, Plan 2026-10-27-p4-hebel-p5-kern Paket B)
 * — EINE Tabelle für Pfad UND Parameter. Vorher verlor `<Navigate to="/webinar-auswertungen">` die Query (Lesezeichen mit
 * Zeitraum oder Reiter landeten auf der Standardansicht).
 *
 * Reines Modul ohne React: `umleitungsZiel(pfad, suche)` → Ziel-URL oder `null` (= alte Seite zeigen). Komponente in
 * `Umleitung.tsx`, Test `umleitungsTabelle.test.ts`.
 *
 * NICHT umgeleitet (Funktionen laut Checkliste nicht vollständig umgezogen, Till 27.10.2026): /anfo-statistics,
 * /webinar-user-verhalten, /webinar-auswertungen ohne Reiter bzw. Reiter 0/3/4 — dort steht oben ein Hinweis.
 * Die alte Anfo-Übersicht bleibt bis zur Abnahme unter /plx-orders/alt erreichbar (Hinweis + Seitenleiste).
 */

/** Alte Reiter von /webinar-auswertungen (bzw. /webinar-statistics), die jetzt unter /auswertungen/werkzeuge stehen. */
export const ALT_TAB_ZU_WERKZEUG: Record<string, string> = {
  '1': 'zuordnungen',
  '2': 'affiliate',
  '5': 'design-ab',
  '6': 'formeln',
};

/** Filter der alten /plx-orders, die /auswertungen/bestellungen NICHT kennt (dort gilt nur der Zeitraum). */
export const PLX_FILTER_OHNE_ENTSPRECHUNG = [
  'artikel',
  'vertrag_sts',
  'banner',
  'werbeaktion_wbkz',
  'subscription_type',
  'source',
  'blog',
  'banner_contains',
  'banner_not_contains',
  'excludeTestOrders',
] as const;

/** Lesbare Namen der Alt-Filter für den Hinweis auf /auswertungen/bestellungen („Produkt FMM"). */
const ALT_FILTER_NAME: Record<(typeof PLX_FILTER_OHNE_ENTSPRECHUNG)[number], string> = {
  artikel: 'Produkt',
  vertrag_sts: 'Status',
  banner: 'Banner',
  werbeaktion_wbkz: 'Werbeaktion',
  subscription_type: 'Abo-Typ',
  source: 'Quelle',
  blog: 'Blog',
  banner_contains: 'Banner enthält',
  banner_not_contains: 'Banner enthält nicht',
  excludeTestOrders: 'Testbestellungen',
};

/**
 * Alt-Filter, die als Suchbegriff in die Bestellliste übernommen werden (Reihenfolge = Vorrang): Produkt, Werbeaktion,
 * Banner-Teil. Nur ein EINZELNER Wert — „TAM,FUM" ist eine Oder-Auswahl, die Suche verknüpft Wörter mit Und.
 */
const ALT_FILTER_ALS_SUCHE = ['artikel', 'werbeaktion_wbkz', 'banner_contains'] as const;

export function sucheAusAltFiltern(p: URLSearchParams): string | null {
  for (const k of ALT_FILTER_ALS_SUCHE) {
    const v = p.get(k)?.trim();
    if (v && !v.includes(',') && v !== 'all') return v;
  }
  return null;
}

/**
 * Hinweis auf /auswertungen/bestellungen nach einer Umleitung mit Alt-Filtern (P5-Review 27.10.2026): der Link auf die
 * alte Übersicht trägt den AKTUELL gewählten Zeitraum (nicht den des alten Links) und alle Alt-Filter; `filter` nennt
 * sie lesbar („Produkt FMM").
 */
/**
 * Ist dieser Alt-Parameter ein echter Filter, den die neue Seite nicht kennt? Leer und „all" filtern nichts;
 * Testbestellungen blendet auch die neue Liste aus — nur „eingeblendet" (`false`) ist eine Abweichung.
 */
const istAltFilter = (k: (typeof PLX_FILTER_OHNE_ENTSPRECHUNG)[number], v: string | null): boolean =>
  v !== null && v.trim() !== '' && v !== 'all' && (k !== 'excludeTestOrders' || v === 'false');

export function altFilterHinweis(alt: string, von: string, bis: string): { link: string; filter: string[] } {
  const p = new URLSearchParams(alt);
  p.set('startDate', von);
  p.set('endDate', bis);
  const filter = PLX_FILTER_OHNE_ENTSPRECHUNG.filter((k) => istAltFilter(k, p.get(k))).map((k) =>
    k === 'excludeTestOrders' ? 'Testbestellungen eingeblendet' : `${ALT_FILTER_NAME[k]} ${p.get(k)}`,
  );
  return { link: `/plx-orders/alt?${p.toString()}`, filter };
}

/** Unterseiten, die 1:1 eingebettet sind: alle Parameter (mode, partial, tab, quarter, startDate, endDate …) bleiben. */
const EINGEBETTET: Record<string, string> = {
  '/plx-orders/kuendigungsquoten': '/auswertungen/umsatz/kuendigung',
  '/plx-orders/cashflow-forecast': '/auswertungen/umsatz/cashflow',
  '/plx-orders/revenue-variance': '/auswertungen/umsatz/diskrepanzen',
  '/plx-orders/anfo-valuation': '/auswertungen/umsatz/anfo-bewertung',
};

const mitQuery = (pfad: string, p: URLSearchParams): string => {
  const s = p.toString();
  return s ? `${pfad}?${s}` : pfad;
};

/** Nur diese Schlüssel übernehmen (in dieser Reihenfolge). */
const nur = (p: URLSearchParams, schluessel: string[]): URLSearchParams => {
  const out = new URLSearchParams();
  for (const k of schluessel) {
    const v = p.get(k);
    if (v !== null && v !== '') out.set(k, v);
  }
  return out;
};

export function umleitungsZiel(pfadRoh: string, suche: string): string | null {
  const pfad = pfadRoh.length > 1 ? pfadRoh.replace(/\/+$/, '') : pfadRoh;
  const p = new URLSearchParams(suche.startsWith('?') ? suche.slice(1) : suche);

  if (pfad === '/plx-orders') {
    const ziel = nur(p, ['startDate', 'endDate']);
    // Filter ohne Entsprechung: die neue Seite nennt sie und verlinkt die alte Übersicht MIT ihnen.
    if (PLX_FILTER_OHNE_ENTSPRECHUNG.some((k) => istAltFilter(k, p.get(k)))) ziel.set('alt', p.toString());
    // Produkt / Werbeaktion / Banner-Teil als Suchbegriff der Bestellliste vorbelegen (bleibt in der URL).
    const suche = sucheAusAltFiltern(p);
    if (suche) ziel.set('suche', suche);
    return mitQuery('/auswertungen/bestellungen', ziel);
  }

  if (EINGEBETTET[pfad]) return mitQuery(EINGEBETTET[pfad], p);

  const detail = /^\/webinar-auswertungen\/([^/]+)$/.exec(pfad);
  if (detail) return `/auswertungen/webinare/${detail[1]}`;

  if (pfad === '/webinar-auswertungen' || pfad === '/webinar-statistics') {
    // Alter Link aus dem Bestell-Detail: /webinar-statistics?webinar=<id>.
    const webinar = p.get('webinar');
    if (webinar && /^[A-Za-z0-9_-]{1,64}$/.test(webinar)) return `/auswertungen/webinare/${webinar}`;
    const werkzeug = ALT_TAB_ZU_WERKZEUG[p.get('tab') ?? ''];
    if (werkzeug) {
      const ziel = nur(p, ['startDate', 'endDate']);
      ziel.set('tab', werkzeug);
      return mitQuery('/auswertungen/werkzeuge', ziel);
    }
    // Reiter 0/3/4 und ohne Reiter: alte Seite (nicht vollständig umgezogen), /webinar-statistics als Alias MIT Query.
    return pfad === '/webinar-statistics' ? mitQuery('/webinar-auswertungen', p) : null;
  }

  return null;
}
